"use strict";
import * as WebBrowser from "expo-web-browser";
import { colorsLight } from "ui/src/theme";
import { NATIVE_TOKEN_PLACEHOLDER } from "constants/uniswapAddresses";
import { Chain } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { getChainInfo } from "features/chains/chainInfo";
import { toGraphQLChain } from "features/chains/utils";
import { isNativeCurrencyAddress } from "utils/uniswapCurrencyId";
import { canOpenURL, openURL } from "utils/link";
import { logger } from "utilities/src/logger/logger";
const ALLOWED_EXTERNAL_URI_SCHEMES = ["http://", "https://"];
export async function openUri(uri, openExternalBrowser = false, isSafeUri = false, controlsColor = colorsLight.accent1) {
  const trimmedURI = uri.trim();
  if (!isSafeUri && !ALLOWED_EXTERNAL_URI_SCHEMES.some((scheme) => trimmedURI.startsWith(scheme))) {
    logger.error(new Error("User attempted to open potentially unsafe url"), {
      tags: {
        file: "linking",
        function: "openUri"
      },
      extra: { uri }
    });
    return;
  }
  const isHttp = /^https?:\/\//.test(trimmedURI);
  const supported = isHttp ? true : await canOpenURL(uri);
  if (!supported) {
    logger.warn("linking", "openUri", `Cannot open URI: ${uri}`);
    return;
  }
  try {
    if (openExternalBrowser) {
      await openURL(uri);
    } else {
      await WebBrowser.openBrowserAsync(uri, {
        // iOS only
        controlsColor,
        presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
        // Android only
        // This is needed to avoid the browser automatically closing when the user comes back from another app (for example, when using the camera during FOR KYC).
        showInRecents: true,
        // Web only
        windowFeatures: "popup=false"
      });
    }
  } catch (error) {
    logger.error(error, { tags: { file: "linking", function: "openUri" }, extra: { uri } });
  }
}
export var ExplorerDataType = /* @__PURE__ */ ((ExplorerDataType2) => {
  ExplorerDataType2["TRANSACTION"] = "transaction";
  ExplorerDataType2["TOKEN"] = "token";
  ExplorerDataType2["ADDRESS"] = "address";
  ExplorerDataType2["BLOCK"] = "block";
  ExplorerDataType2["NFT"] = "nft";
  ExplorerDataType2["NATIVE"] = "native";
  return ExplorerDataType2;
})(ExplorerDataType || {});
export function getExplorerLink(chainId, data, type) {
  const { explorer, nativeCurrency } = getChainInfo(chainId);
  const prefix = explorer.url;
  switch (type) {
    case "transaction" /* TRANSACTION */:
      return `${prefix}tx/${data}`;
    case "token" /* TOKEN */:
      if (data === nativeCurrency.address && nativeCurrency.explorerLink) {
        return nativeCurrency.explorerLink ?? `${prefix}token/${data}`;
      }
      return `${prefix}token/${data}`;
    case "block" /* BLOCK */:
      return `${prefix}block/${data}`;
    case "address" /* ADDRESS */:
      return `${prefix}address/${data}`;
    case "nft" /* NFT */:
      return `${prefix}nft/${data}`;
    default:
      return `${prefix}`;
  }
}
export function getTokenDetailsURL({
  address,
  chain,
  chainUrlParam,
  inputAddress
}) {
  if (!chain) {
    return "/not-found";
  }
  const chainInfo = toGraphQLChain(chain);
  const adjustedAddress = isNativeCurrencyAddress(chain, address) ? NATIVE_TOKEN_PLACEHOLDER : address;
  const chainName = chainUrlParam || String(chainInfo)?.toLowerCase() || Chain.Ethereum.toLowerCase();
  const inputAddressSuffix = inputAddress ? `?inputCurrency=${inputAddress}` : "";
  return `/explore/tokens/${chainName}/${adjustedAddress}${inputAddressSuffix}`;
}
export function getPoolDetailsURL(address, chain) {
  const chainName = getChainInfo(chain).urlParam;
  return `/explore/pools/${chainName}/${address}`;
}
